import { Suspense, lazy, useMemo, useState } from 'react';
import { Outlet, Navigate, useParams, createRoutesFromElements } from 'react-router-dom';
import workoutsLibraryRoutes from '@ashn/workouts-library-react/WorkoutsLibraryRoutes';
import ActionPlanRoutes from '@ashn/action-plan-react/Routes';
import healthyDealsRoutes from '@ashn/healthy-deals-react/HealthyDealsRoutes';
import HealthyDealsSkeleton from '@ashn/healthy-deals-react/Skeletons';
import getLiveSessionChildRoutes from '@ashn/live-session-react/Routes';
import useNavRoutes from '../../data/queryHooks/useNavRoutes';
import PaddedContainer from './containers/PaddedContainer';
import PageSEO from './PageSEO';
import { Programs, SessionStorageKey } from '../../scripts/miscConstants';
import useHomePage from '../../data/queryHooks/useHomePage';
import { GetHomePageQuery } from '../../scripts/graphqlQueries';
import useSessionInfo from '../../data/queryHooks/useSessionInfo';
import useClientData from '../../data/queryHooks/useClientData';
import useFeatureFlags from '../../data/queryHooks/useFeatureFlags';
import ProtectedRoute from './ProtectedRoute';
import getApiEnvironment from '../../utils/environmentHelpers/getAPIEnvironments';
import Layout from './Layout';
import ScrollToTop from './ScrollToTop';
import FlowGuard from './FlowGuard';
import { staticIdentityRoutes } from '@ashn/identity-react/Identity';
import NotFoundErrorPage from './ErrorPages/NotFoundErrorPage';
import { fetchClientBlob } from '../../data/apiCalls';
import LearnPage from '../learn/LearnPage';

const PaymentPage = lazy(() => import('../memberSessions/PaymentPage'));

const RescheduleAppointmentPage = lazy(() =>
	import('../memberSessions/RescheduleAppointmentPage')
);

const ConfirmSOSPage = lazy(() => import('../memberSessions/ConfirmSOSPage'));

const WaitingRoomPage = lazy(() => import('../memberSessions/WaitingRoomPage'));

const AppointmentDetailsPage = lazy(() =>
	import('../memberSessions/AppointmentDetailsPage')
);

const NotificationPreferencesPage = lazy(() =>
	import('../notificationPreferences/NotificationPreferencesPage')
);

const PaymentHistoryPage = lazy(() => import('../billing/PaymentHistoryPage'));

const PaymentAndBilling = lazy(() =>
	import('../billing/PaymentAndBillingPage')
);

const ProgramDetailPage = lazy(() =>
	import('../programDetailPage/ProgramDetailPage')
);
const ProgramDetailPageAnthemPreLogin = lazy(() =>
	import('../programDetailPage/anthem/ProgramDetailPageAnthemPreLogin')
);

const ProgramDetailPageAnthemPostLogin = lazy(() =>
	import(
		'../dashboard/VirtualFitnessAndRecovery/anthem/ProgramDetailPageAnthemPostLogin'
	)
);

const GenericStorePage = lazy(() =>
	import('../genericStorePage/GenericStorePage')
);

const SecureMessaging = lazy(() =>
	import('../secureMessaging/SecureMessaging')
);

const TermsAgreement = lazy(() =>
	import('./TermsAgreementWrapper')
);

const BuyHealthyPage = lazy(() =>
	import('../buyHealthy/BuyHealthyPage')
);

const RedirectToVptot = () => {
	const { clientCode } = useParams();
	return <Navigate to={`/vptot/${clientCode}`} replace />;
};

const SupportChatbot = lazy(() =>
	import('../intercomChat/SupportChatbot')
);

const MyAccountPage = lazy(() => import('../myAccountPage/MyAccountPage'));

const MyFormsPage = lazy(() => import('../memberSessions/MyFormsPage'));

const buyHealthyTitle = 'BuyHealthy Marketplace';
const buyHealthyDescription = 'Discounts on popular brands to support your health, wellness, and fitness journey.';

const useReactRoutes = () => {
	const { intercomChatEnabled } = useFeatureFlags();

	const {
		homePageRoute,
		topNavBarRoutes,
		secondNavBarRoutes,
		errorPages,
		footerMenu
	} = useNavRoutes();

	const {
		ssId, memberPrograms, paymentHistoryBlob,
		phoneNumber, secureMessaging, hasBuyHealthyVendorDiscounts,
		hasEnableMessageCenterEmailNotification, flows, email,
	} = useSessionInfo();

	// We only want react-router tags for React pages internal to this app, not for Ember
	// pages or external links. The AshCare React pages will have a non-null component.
	// Entries with reactRoute: false have their routes defined explicitly below.
	const reactLinks = [
		...homePageRoute,
		...topNavBarRoutes,
		...secondNavBarRoutes,
		...errorPages,
		...footerMenu,
	].filter(link => link.component && link.reactRoute !== false);

	const { data } = useHomePage(GetHomePageQuery);
	const { clientData } = useClientData();

	const [appFlows, setAppFlows] = useState(flows);

	const routes = useMemo(() => [
		{
			path: '/empowered-decisions/anthem',
			element: <Navigate to="/anthem" replace />,
		},
		{
			path: '/vptr',
			element: <Navigate to="/vptot" replace />,
		},
		{
			path: '/vptr/:clientCode',
			element: <RedirectToVptot />,
		},
		{
			element: (
				<>
					<ScrollToTop />
					{intercomChatEnabled && (
						<SupportChatbot />
					)}
					<FlowGuard appFlows={appFlows}>
						<Outlet />
					</FlowGuard>
				</>
			),
			children: [
				{
					element: (
						<Layout showHeader showFooter />
					),
					children: [
						{
							path: '/anthem',
							element: <ProgramDetailPageAnthemPreLogin />,
						},
					],
				},
				{
					element: (
						<Layout showHeader showNav showFooter />
					),
					children: [
						...reactLinks.map(link => ({
							path: `/${link.href}`,
							element: link.component,
						})),
						// add routes for custom clientcode program detail pages
						// loop through custom client only
						...(clientData ?? []).flatMap(client =>
							(client.customProgramCardsCollection?.items ?? []).map(program => {
								const slug = program.overridePage?.slug || program.detailPage?.slug;
								const pageId = program.overridePage?.sys?.id || program.detailPage?.sys?.id;
								return slug ? { path: `${slug}/${client.clientCode}`, element: <ProgramDetailPage pageId={pageId} /> } : null;
							}).filter(Boolean)
						),
						// add routes for program detail pages
						...(data?.ashCareHomePageCollection?.items[0]?.programCardsCollection?.items ?? []).map(program =>
							program.detailPage.isRoutable && program.detailPage.slug ? {
								path: program.detailPage.slug,
								element: <ProgramDetailPage pageId={program.detailPage.sys.id} />,
							} : null
						).filter(Boolean),

						// Note: add protected route componnets under this condition

						// Terms Agreement route: Displayed upon login
						{
							path: 'termsAgreement',
							element: (
								<ProtectedRoute>
									<PageSEO
										pageTitle="Latest Terms & Conditions"
										description="Read and Agree ASHCare’s most up-to-date terms, conditions, and disclaimers."
									/>
									<PaddedContainer>
										<TermsAgreement appFlows={appFlows} setAppFlows={setAppFlows} />
									</PaddedContainer>
								</ProtectedRoute>
							)
						},
						{
							path: '/memberSessions/waitingRoom/:appointmentId',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<WaitingRoomPage ssId={ssId} phoneNumber={phoneNumber} />
								</ProtectedRoute>
							),
						},
						{
							path: '/memberSessions/payment/:appointmentId',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<PaymentPage ssId={ssId} phoneNumber={phoneNumber} />
								</ProtectedRoute>
							),
						},
						{
							path: '/memberSessions/payment/confirmsos/:appointmentId',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<ConfirmSOSPage ssId={ssId} />
								</ProtectedRoute>
							),
						},
						{
							path: '/memberSessions/reschedule/:appointmentId',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<RescheduleAppointmentPage ssId={ssId} phoneNumber={phoneNumber} />
								</ProtectedRoute>
							),
						},
						{
							path: '/memberSessions/appointment',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<AppointmentDetailsPage />
								</ProtectedRoute>
							),
						},
						{
							path: '/memberSessions/myForms',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
							<MyFormsPage ssId={ssId} email={email} />
								</ProtectedRoute>
							),
						},
						{
							path: '/message-center',
							element: (
								<ProtectedRoute
									isAuthorized={
										memberPrograms.length > 0 && secureMessaging
									}
								>
									<Suspense>
										<SecureMessaging />
									</Suspense>
								</ProtectedRoute>
							),
						},
						{
							path: 'actionPlan/*', // If you change this path, update all "pathToActionPlanRoutes" props in this project to match
							element: (
								<PaddedContainer>
									<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
										<ActionPlanRoutes
											ssId={ssId}
											apiEnv={getApiEnvironment()}
											navigateToResourceLibrary={resourceId => {
												window.location.assign(`/resourcelibrary/workouts/${resourceId}`);
											}}
											pathToActionPlanRoutes="/actionPlan"
										/>
									</ProtectedRoute>
								</PaddedContainer>
							),
						},
						{
							path: 'myAccount/info',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.length > 0}>
									<Suspense>
										<MyAccountPage />
									</Suspense>
								</ProtectedRoute>
							),
						},
						{
							path: 'notificationPreferences',
							element: (
								<ProtectedRoute isAuthorized={hasEnableMessageCenterEmailNotification}>
									<Suspense>
										<NotificationPreferencesPage />
									</Suspense>
								</ProtectedRoute>
							),
						},
						{
							path: 'payment/history/*',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<Suspense>
										<PaymentHistoryPage ssId={ssId} paymentHistoryBlob={paymentHistoryBlob} />
									</Suspense>
								</ProtectedRoute>
							),
						},
						{
							path: 'payment/billing/*',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualPhysicalTherapyAndRehabilitation)}>
									<Suspense>
										<PaymentAndBilling ssId={ssId} paymentHistoryBlob={paymentHistoryBlob} />
									</Suspense>
								</ProtectedRoute>
							),
						},
						{
							path: '/',
							element: (
								<ProtectedRoute isAuthorized={memberPrograms.includes(Programs.VirtualFitnessAndRecovery)}>
									<ProgramDetailPageAnthemPostLogin />
								</ProtectedRoute>
							),
						},
						{
							path: 'generic-store',
							element: <GenericStorePage />,
						},
						{
							path: '/buyhealthy/*',
							element: (
								<ProtectedRoute isAuthorized={hasBuyHealthyVendorDiscounts}>
									<Suspense fallback={
										<HealthyDealsSkeleton pageTitle={buyHealthyTitle} description={buyHealthyDescription} />
									}>
										<BuyHealthyPage pageTitle={buyHealthyTitle} />
									</Suspense>
								</ProtectedRoute>
							),
							id: 'healthyDealsRoutesData',
							loader: () => ({
								apiEnvironment: getApiEnvironment(),
								ssId,
								componentRootPath: '/buyhealthy',
								updatePageTitleAndDescription: (title, description) => {
									document.title = `${title} | ASHCare`;
									const metaDescription = document.querySelector(
										'meta[name="description"]'
									);
									if (metaDescription) {
										metaDescription.setAttribute('content', description);
									}
								},
								setMainTitleTo: buyHealthyTitle,
								description: buyHealthyDescription,
								hasOwnProvider: false,
								redirectToLogin: () => {
									window.location.href = '/identity/login';
								},
							}),
							children: healthyDealsRoutes,
						},
						{
							id: 'workoutsLibraryRoutesData',
							path: '/resourcelibrary/workouts',
							element: (
								<LearnPage>
									<Outlet />
								</LearnPage>
							),
							HydrateFallback: () => null,
							loader: () => ({
								ssId,
								workoutsLibraryMainHeading: 'Learn',
								siteNameForTitleSuffix: ' | ASHCare',
							}),
							children: workoutsLibraryRoutes,
						},
						{
							path: 'identity',
							id: 'identity',
							errorElement: <NotFoundErrorPage />,
							children: staticIdentityRoutes,
							loader: async ({ request }) => {
								// get the current path to determine if we're on the registration page
								const { pathname } = new URL(request.url);
								const normalizedPathname = pathname.replace(/\/+$/, '') || '/';
								const isRegistrationRoute = normalizedPathname === '/identity/registration';

								let blob = null;

								if (isRegistrationRoute && !ssId) {
									const clientCode = sessionStorage.getItem(SessionStorageKey.clientCode);
									const matchingClient = clientData?.find(client =>
										client.clientCode?.toLowerCase() === clientCode?.toLowerCase()
									);
									const requiresRegistrationTokenization =
										matchingClient?.requiresRegistrationTokenization === true;

									if (clientCode && requiresRegistrationTokenization) {
										const clientBlob = await fetchClientBlob(clientCode);
										if (!clientBlob?.blob) {
											throw new Error('Tokenized registration requires a client blob');
										}

										blob = clientBlob.blob;
									}
								}

								const siteBase = `https://${getApiEnvironment() === 'prod' ? 'www' : getApiEnvironment()}.ashcare.com`;

								return {
									apiEnvironment: getApiEnvironment(),
									ssId,
									clientId: clientData?.clientId || 'ashcare.code',
									hasOwnProvider: true,
									logOutLink: '/auth/logout',
									redirectUri: `${siteBase}/auth/callback`,
									path: 5,
									termsURL: '/terms',
									contactURL: '/contactus',
									customerServicePhoneNumber: phoneNumber || '844-641-2746',
									unauthorizedCallback: () => {
										const encodedUrl = encodeURIComponent(window.location.href);
										window.location.href = `/identity/login?redirect=${encodedUrl}`;
									},
									isAuthenticated: !!ssId,
									registrationFormLanguage: 'Enter the following information to confirm that you are eligible to participate in the ASHCare program.',
									duplicateCheckEnabled: true,
									enableSSO: true,
									blob,
								}
							},

						}
					],
				},
				// Below route(s) will not have header, nav and footer
				{
					element: (
						<Layout />
					),
					children: [
						{
							element: (
								<ProtectedRoute>
									<>
										<PageSEO
											pageTitle="Live Synchronous Session"
											description="Live synchronous video session between member and provider"
										/>
										<Outlet />
									</>
								</ProtectedRoute>
							),
							children: createRoutesFromElements(getLiveSessionChildRoutes(getApiEnvironment(), ssId)),
						},
					],
				},
			],
		},
	// eslint-disable-next-line react-hooks/exhaustive-deps
	], [clientData, ssId]);

	return routes;
};

export default useReactRoutes;
